import { useCallback, useEffect, useMemo, useState } from 'react';

import analyticsService from '../../../../services/analyticsService';

import type { IntentDistributionResponse, StudentIntentDistribution } from './types';

type UseIntentDistributionSectionProps = {
  allCourseIds: string[];
  classValue: string;
  groupCourseMap: Record<string, string[]>;
  timePeriodValue: string;
};

type UseIntentDistributionSectionReturn = {
  data: IntentDistributionResponse | null;
  error: string | null;
  loading: boolean;
  students: StudentIntentDistribution[];
};

function getDateRange(timePeriodValue: string): { startDate: string | undefined; endDate: string | undefined } {
  const now = new Date();
  const endDate = now.toISOString();
  let startDate: Date;

  switch (timePeriodValue) {
    case '7d':
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case '30d':
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case '3m':
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 3);
      break;
    default:
      return { startDate: undefined, endDate: undefined };
  }

  return { startDate: startDate.toISOString(), endDate };
}

function useIntentDistributionSection({
  allCourseIds,
  classValue,
  groupCourseMap,
  timePeriodValue,
}: UseIntentDistributionSectionProps): UseIntentDistributionSectionReturn {
  const [data, setData] = useState<IntentDistributionResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const currentCourseIds = useMemo(() => {
    return classValue === 'all' ? allCourseIds : groupCourseMap[classValue] || [];
  }, [classValue, allCourseIds, groupCourseMap]);

  const { startDate: currentStartDate, endDate: currentEndDate } = useMemo(() => {
    return getDateRange(timePeriodValue);
  }, [timePeriodValue]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result: IntentDistributionResponse = await analyticsService.getIntentDistribution({
        courseIds: currentCourseIds,
        startDate: currentStartDate,
        endDate: currentEndDate,
      });

      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [currentCourseIds, currentStartDate, currentEndDate]);

  useEffect(() => {
    if (allCourseIds.length > 0) {
      fetchData();
    } else {
      setLoading(false);
      setData(null);
    }
  }, [fetchData, allCourseIds.length]);

  const students = useMemo(() => {
    return data?.students || [];
  }, [data]);

  return {
    data,
    error,
    loading,
    students,
  };
}

export default useIntentDistributionSection;
